import { defineStore, storeToRefs } from "pinia";
import Game from "@/game_logic/game";
import { computed, ref, watch } from "vue";
import { parseMoveStringToIndices } from "@/game_logic/utils/parseMove";
import calculateNewRatings from "@/game_logic/rating";
import { useUserInfoStore } from "@/stores/userInfoStore";

type GameModes = "local" | "online";

export type GameInfo = {
  attackerID?: string;
  defenderID?: string;
  moves?: { [move: string]: string };
};

export type PlayerInfo = {
  name?: string;
  profilePic?: string;
  rating?: number;
};

export const useGameStore = defineStore("gameStore", () => {
  const userStore = useUserInfoStore();
  const { uid } = storeToRefs(userStore);
  const gameId = ref<string | null>(null);
  const error = ref<string | null>(null);
  const waitingMessage = ref<string>("");
  const isAttacker = ref<boolean>(true);
  const gameState = ref<Game>(new Game(isAttacker.value));
  const gameMode = ref<GameModes>();
  const gameInfo = ref<GameInfo>();
  const opponentInfo = ref<{
    id?: string;
    name?: string;
    profilePic?: string;
    rating?: number;
  }>({});
  let latestMove = "";

  const isAttackersTurn = computed(
    () => (gameState.value?.moveHistory.length || 0) % 2 === 0
  );

  const isMyTurn = computed(
    () =>
      gameMode.value === "local" || isAttacker.value === isAttackersTurn.value
  );

  const isGameOver = computed(() => gameState.value.isGameOver);

  const setGameId = (id: string | null) => {
    gameId.value = id;
  };

  const setGameInfo = (newGameInfo: GameInfo, opponentId?: string) => {
    gameInfo.value = newGameInfo;
    if (!opponentId) return;

    if (isAttacker.value) {
      gameInfo.value.defenderID = opponentId;
    } else {
      gameInfo.value.attackerID = opponentId;
    }
  };

  const setErrorMessage = (message: string | null) => {
    error.value = message;
  };

  const setWaitingMessage = (message: string) => {
    waitingMessage.value = message;
  };

  const setOpponentInfo = (
    newId: string,
    name?: string,
    profilePic?: string,
    rating?: number
  ) => {
    opponentInfo.value = {
      ...opponentInfo.value,
      id: newId,
      name,
      profilePic,
      rating,
    };
  };

  const getOpponentID = () => opponentInfo.value.id;

  const initializeLocalGame = () => {
    gameMode.value = "local";
    gameState.value = new Game(true);
    waitingMessage.value = "";
  };

  const initializeOnlineGame = () => {
    console.log("Initializing online game");
    isAttacker.value = gameInfo.value?.["attackerID"] === uid.value;
    gameMode.value = "online";
    gameState.value = new Game(isAttacker.value);
    waitingMessage.value = "";
  };

  const makeLocalMove = (
    startRow: number,
    startCol: number,
    endRow: number,
    endCol: number
  ) => {
    if (!gameState.value) return;
    gameState.value.processMove(startRow, startCol, endRow, endCol);
  };

  const processMove = (moveString: string) => {
    if (moveString === latestMove) return;
    latestMove = moveString;
    const move = parseMoveStringToIndices(moveString);
    gameState.value?.processMove(...move);
  };

  watch(
    () => isGameOver.value,
    (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        if (gameMode.value === "online") {
          const newRating = calculateNewRatings(
            isAttacker.value ? userStore.rating : opponentInfo.value.rating!,
            isAttacker.value ? opponentInfo.value.rating! : userStore.rating,
            gameState.value.isAttackerWinner!
          )[isAttacker.value ? 0 : 1];
          userStore.setRating(newRating);
        }
      }
    }
  );

  return {
    gameId,
    gameInfo,
    error,
    gameState,
    gameMode,
    isGameOver,
    isAttacker,
    opponentInfo,
    isMyTurn,
    isAttackersTurn,
    waitingMessage,
    getOpponentID,
    setGameId,
    setGameInfo,
    setOpponentInfo,
    setWaitingMessage,
    setErrorMessage,
    initializeLocalGame,
    initializeOnlineGame,
    makeLocalMove,
    processMove,
  };
});
